import { useEffect, useState } from 'react';
import api from '../services/api';

/**
 * AnalyticsLoader - Injects custom head/body scripts from admin settings
 * This component loads scripts once on app initialization and injects them into the DOM
 */
const AnalyticsLoader = () => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (loaded) return;

    const loadAnalyticsScripts = async () => {
      try {
        const res = await api.get('/settings/analytics-scripts');
        const { head_scripts, body_scripts } = res.data;

        // Inject head scripts
        if (head_scripts && head_scripts.trim()) {
          // Create a temporary container to parse the HTML
          const tempHead = document.createElement('div');
          tempHead.innerHTML = head_scripts;
          
          // Process each child element
          Array.from(tempHead.children).forEach(child => {
            if (child.tagName === 'SCRIPT') {
              // For script tags, we need to recreate them to execute
              const script = document.createElement('script');
              
              // Copy attributes
              Array.from(child.attributes).forEach(attr => {
                script.setAttribute(attr.name, attr.value);
              });
              
              // Copy inline content
              if (child.innerHTML) {
                script.innerHTML = child.innerHTML;
              }
              
              // Add data attribute to identify injected scripts
              script.setAttribute('data-analytics-injected', 'head');
              
              document.head.appendChild(script);
            } else {
              // For non-script elements, just append
              child.setAttribute('data-analytics-injected', 'head');
              document.head.appendChild(child.cloneNode(true));
            }
          });
        }

        // Inject body scripts (right after body tag)
        if (body_scripts && body_scripts.trim()) {
          const tempBody = document.createElement('div');
          tempBody.innerHTML = body_scripts;
          
          // Get the first child of body (should be our root or google translate element)
          const firstChild = document.body.firstChild;
          
          Array.from(tempBody.children).forEach(child => {
            if (child.tagName === 'SCRIPT') {
              const script = document.createElement('script');
              Array.from(child.attributes).forEach(attr => {
                script.setAttribute(attr.name, attr.value);
              });
              if (child.innerHTML) {
                script.innerHTML = child.innerHTML;
              }
              script.setAttribute('data-analytics-injected', 'body');
              document.body.insertBefore(script, firstChild);
            } else if (child.tagName === 'NOSCRIPT') {
              // Handle noscript tags specially
              const noscript = document.createElement('noscript');
              noscript.innerHTML = child.innerHTML;
              noscript.setAttribute('data-analytics-injected', 'body');
              document.body.insertBefore(noscript, firstChild);
            } else {
              child.setAttribute('data-analytics-injected', 'body');
              document.body.insertBefore(child.cloneNode(true), firstChild);
            }
          });
        }

        setLoaded(true);
      } catch (error) {
        // Silently fail - don't block app from loading
        console.log('Analytics scripts not loaded:', error.message);
        setLoaded(true);
      }
    };

    loadAnalyticsScripts();
  }, [loaded]);

  // This component doesn't render anything
  return null;
};

export default AnalyticsLoader;
